<template>
  <el-container>
    <el-aside v-if="uploadProgress === UploadProgress.Select || !loadingDataValid" class="withFooterAndHeader" width="14rem">
      <el-scrollbar>
        <el-form ref="dataForm" :model="loadingData" label-position="top" status-icon>
          <el-form-item v-if="referenceData && referenceData.length > 0" prop="referenceCategory" :label="$t('components.mesh-loader.reference-category')">
            <el-select v-model="loadingData.referenceCategory">
              <el-option value="" :label="$t(`enum.upload-category.none`)" />
              <el-option v-for="category in Object.values(UploadCategory)" :key="category" :value="category" :label="$t(`enum.upload-category.${category}`)" />
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="referenceData && referenceData.length > 0 && loadingData.referenceCategory !== null"
            prop="reference"
            :label="$t('components.mesh-loader.reference')"
          >
            <el-select v-model="loadingData.referenceId">
              <el-option v-for="reference in categoryReferences" :key="reference" :value="reference.componentId" :label="reference.name" />
            </el-select>
          </el-form-item>

          <!-- Category Form -->
          <el-form-item prop="category" :label="$t('components.mesh-loader.category')">
            <el-select v-model="loadingData.category">
              <el-option v-for="category in Object.values(UploadCategory)" :key="category" :value="category" :label="$t(`enum.upload-category.${category}`)" />
            </el-select>
          </el-form-item>

          <!-- Mesh Name -->
          <el-form-item
            prop="title"
            :label="$t('components.mesh-loader.title')"
            :rules="{
              required: true,
              message: $t('error.vuelidate.required'),
              trigger: 'blur',
            }"
          >
            <el-input v-model="loadingData.title" />
          </el-form-item>

          <!-- Upload 3D Object  -->
          <el-form-item
            prop="url"
            :label="$t('components.mesh-loader.mesh')"
            :rules="{
              required: true,
              message: $t('error.vuelidate.required'),
              trigger: 'blur',
            }"
          >
            <el-upload
              action="#"
              drag
              list-type="picture-card"
              :before-upload="beforeUpload"
              :http-request="(res: any) => uploadFile(res)"
              accept=".stl,.obj,.fbx,.mtl"
            >
              <template #default>
                <div>
                  <font-awesome-icon icon="upload" class="primary big" />
                  <br />
                  {{ $t('components.mesh-loader.upload') }}
                </div>
              </template>
              <template #file="{ file }">
                <PreviewRenderer
                  :meshes="[
                    {
                      filetype: getFileType(file.name),
                      filename: file.name,
                      url: file.url,
                      raw: file.raw,
                    },
                  ]"
                  :is-selected="selected === file.uid"
                  :can-remove="true"
                  @remove-item="handleRemove(file)"
                  @select-item="handleSelect(file)"
                />
              </template>
            </el-upload>
          </el-form-item>
        </el-form>
      </el-scrollbar>
    </el-aside>
    <el-aside v-if="uploadProgress === UploadProgress.Pivot" class="withFooterAndHeader" width="14rem">
      <el-scrollbar>
        <el-steps :key="boneKey" direction="vertical" :active="activeBoneIndex">
          <draggable v-model="boneData" handle=".el-step__icon" item-key="id" @end="dragDone">
            <template #item="{ element, index }">
              <el-step @click="activeBoneIndex = index">
                <template #title>
                  <span class="media">
                    <el-input v-model="element.name" class="media-content" :placeholder="$t('components.mesh-loader.boneName')" />
                    <span v-if="boneData.length > 2" class="action media-right" @click="removeBone(index)">
                      <font-awesome-icon icon="trash" />
                    </span>
                  </span>
                </template>
              </el-step>
            </template>
          </draggable>
        </el-steps>
        <div class="flexRemaining"></div>
        <div class="addButton">
          <el-button type="primary" circle @click="addBone(null)">
            <font-awesome-icon icon="plus" />
          </el-button>
        </div>
      </el-scrollbar>
    </el-aside>
    <el-main>
      <MeshEditor
        ref="meshEditor"
        v-model="selectedInput"
        :can-toggle-pivot-mode="false"
        :can-select="false"
        :can-toggle-bones="false"
        :can-undo="uploadProgress !== UploadProgress.Select"
        :can-deform="false"
        :can-curve="false"
        :can-resolution="false"
        :activate-aline-to-floor="true"
        :activate-bones="uploadProgress === UploadProgress.Pivot"
        :active-editor-mode="activeEditorMode"
        :active-modify-type="uploadProgress === UploadProgress.Orientation ? ModifyType.rotate : ModifyType.translate"
        :reset-on-pivot-changed="true"
        :fit-camera-to-scene="true"
        :default-orientation-axis="uploadProgress === UploadProgress.Orientation ? OrientationAxis.y : OrientationAxis.none"
        :has-toolbar="true"
        :fullscreen="false"
        :show-orientation-gizmo="true"
      />
    </el-main>
  </el-container>

  <div id="formbuilder-dialog" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div id="info-content" class="modal-content">
        <div id="info-body" class="modal-body p-0">
          <iframe src="/formbuilder/index.html" style="width: 100%; height: 100vh; border: none"></iframe>
        </div>
      </div>
    </div>
  </div>
  <div id="design-files-dialog" class="modal fade" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
    <div class="modal-dialog modal-xl">
      <div id="info-content" class="modal-content">
        <div id="info-body" class="modal-body p-3">
          <p>Low Resolution</p>
          <div v-for="(file, index) in engravingImageFilesLR" :key="file.uid" class="row my-2">
            <div class="col-lg-1">
              <span>{{ index + 1 }}</span>
            </div>
            <div class="col-lg-2">
              <img class="img-fluid" :src="file.url" />
            </div>
            <div class="col-lg-6">
              <label :for="`filename-${index}`">File Name</label>
              <input :id="`filename-${index}`" v-model="file.name" type="text" class="form-control" />
            </div>
          </div>
          <hr class="my-3" />
          <p>High Resolution</p>
          <div v-for="(file, index) in engravingImageFilesHR" :key="file.uid" class="row my-2">
            <div class="col-lg-1">
              <span>{{ index + 1 }}</span>
            </div>
            <div class="col-lg-2">
              <img class="img-fluid" :src="file.url" />
            </div>
            <div class="col-lg-6">
              <label :for="`filename-${index}`">File Name</label>
              <input :id="`filename-${index}`" v-model="file.name" type="text" class="form-control" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            class="btn btn-secondary"
            @click="()=>{
              const rervertFilesLR = engravingImageFilesLR.map((file:any)=>({...file, name:file.raw.name}))
              engravingImageFilesLR = rervertFilesLR
              designFilesDialog.hide()
            }"
          >
            Cancel
          </button>
          <button
            class="btn btn-primary"
            @click="()=>{
              const updateFilename = (files: any[], stateFiles: any[])=>{
                return files.map((file)=>{
                  const stateFile = stateFiles.find(f=>f.uid === file.uid) as any
                  const newName = stateFile.name
                  const fileExtension = stateFile?.raw.type.split('/')[1]
                  const hasExtension = newName.split('.')[1]
                  const newThumbnail = hasExtension ? `${newName.split('.')[0]}.${fileExtension}` : `${newName}.${fileExtension}`
                  return {
                    ...file,
                    thumbnail:newThumbnail
                  }
                })
              }
              selectedEngravingImagesLR = updateFilename(selectedEngravingImagesLR, engravingImageFilesLR)
              selectedEngravingImagesHR = updateFilename(selectedEngravingImagesHR, engravingImageFilesHR)
              designFilesDialog.hide()
            }"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-facing-decorator';
import { ElForm, ElMessage, ElMessageBox } from 'element-plus';
import { UploadFile, UploadStatus } from 'element-plus/es/components/upload/src/upload';
import draggable from 'vuedraggable';
import * as THREE from 'three';
import { Buffer } from 'buffer'; // Impor Buffer polyfill
import { cloneDeep } from '@apollo/client/utilities';

import { cleanUpScene, ObjModel, StlModel } from '@/utils/three/importExport';
import MeshEditor from '@/components/three/scenes/MeshEditor.vue';
import { MeshTreeData } from '@/types/ui/MeshTreeData';
import { FileType3D, getFileType, isValidFileType, UploadCategory } from '@/types/enum/upload';
import { UploadProgress } from '@/types/enum/workflow';
import { ModifyType, OrientationAxis } from '@/types/enum/three';
import { EditorModeState } from '@/types/enum/editor';
import PreviewRenderer from '@/components/three/scenes/PreviewRenderer.vue';
import * as TemplateService from '@/services/api/templateService';
import { MeshExportData } from '@/services/api/modelService';
import { DefaultUndoRedo } from '@/types/ui/HistoryList';
import { FileData } from '@/types/enum/file';
import { ProductVideoFiles } from '@/utils/file';
import { OrderData } from '@/views/ConfiguratorPage.vue';

/* eslint-disable @typescript-eslint/no-explicit-any*/

// Set Buffer as global for the browser environment
(window as any).Buffer = Buffer;

export interface FileUploadData {
  name: string;
  url: string;
  uid: number;
  status?: UploadStatus;
}

export interface MeshUploadData {
  category: UploadCategory;
  title: string;
  url: string;
  base64: string;
  fileType: FileType3D;
}

interface UploadData {
  name: string;
  url: string;
  uid: number;
  status?: UploadStatus;
}

interface LoadingData {
  referenceCategory: UploadCategory;
  referenceId: number | null;
  category: UploadCategory;
  title: string;
  fileType: FileType3D;
  url: string;
  base64: string;
}

interface ResultData {
  category: UploadCategory;
  title: string;
  mesh: THREE.Object3D;
  bones: THREE.Bone[];
  referenceId: number | null;
}

export type MeshTreeDataWithUid = MeshTreeData & { uid?: number };
export enum ImageType {
  'object3D' = 'object3D',
  'product' = 'product',
  'engravingLR' = 'engravingLR',
  'engravingHR' = 'engravingHR',
  'video' = 'video',
}

export interface NewOrderData extends OrderData {
  fields: any[];
}

@Component({
  components: {
    PreviewRenderer,
    MeshEditor,
    ObjModel,
    StlModel,
    draggable,
  },
  emits: ['update:uploadProgress'],
})
export default class MeshLoader extends Vue implements DefaultUndoRedo {
  @Prop({ default: UploadProgress.Select }) uploadProgress!: UploadProgress;
  @Prop({ default: null }) templateId!: number | null;
  selected = -1;

    // note: state for mesh 3d file (its for payload ModelService.uploadNewProduct).
    selectedInput: MeshTreeData = new MeshTreeData(null, this);
  // note: state for controlled el upload mesh 3d, keeping ui when clicking prev step
  meshFiles: FileData[] = [];

  // note: state for product image thumbnails refers to selected input format. Separate it from selectedInput state since it needs to be multiple  (it's for payload ModelService.uploadNewProduct).
  selectedProductImages: MeshTreeDataWithUid[] = [];
  // note: state for controlled el upload product image, keeping ui when clicking prev step
  productImageFiles: FileData[] = [];
  productVideoFiles: ProductVideoFiles[] = [];

  // note: state for product image thumbnails refers to selected input format. Separate it from selectedInput state since it needs to be multiple  (it's for payload ModelService.uploadNewProduct).
  selectedEngravingImagesLR: MeshTreeDataWithUid[] = [];
  // note: state for controlled el upload product image, keeping ui when clicking prev step
  engravingImageFilesLR: FileData[] = [];

  selectedEngravingImagesHR: MeshTreeDataWithUid[] = [];
  engravingImageFilesHR: FileData[] = []

  UploadProgress = UploadProgress;
  ModifyType = ModifyType;

  loadingData: LoadingData = {
    referenceCategory: UploadCategory.ReferenceDummy,
    referenceId: null,
    category: UploadCategory.Product,
    title: '',
    fileType: FileType3D.NONE,
    url: '',
    base64: '',
  };
  loadingDataValid = false;

  boneData: THREE.Bone[] = [];
  activeBoneIndex = 0;
  boneKey = 0;

  activeEditorMode: EditorModeState = EditorModeState.readonly;

  referenceData: MeshExportData[] = [];

  UploadCategory = UploadCategory;
  previousUploadProgress = UploadProgress.Select;
  OrientationAxis = OrientationAxis;
  private formbuilderDialog: any = null;
  private designFilesDialog: any = null;
  getFileType = getFileType;

  mounted(): void {
    if (this.templateId) {
      TemplateService.getMeshData(this.templateId).then((result) => (this.referenceData = result));
    }

    this.$nextTick(() => {
      this.activeBoneIndex = 0;
    });
  }

  get categoryReferences(): MeshExportData[] {
    const references = this.referenceData.filter((item) => item.category === this.loadingData.referenceCategory);
    this.loadingData.referenceId = null;
    if (references.length > 0) this.loadingData.referenceId = references[0].componentId;
    return references;
  }

  getResult(): ResultData | null {
    if (this.selectedInput.treeData.length > 0) {
      const treeData = this.selectedInput.treeData[0];
      return {
        category: this.loadingData.category,
        title: this.loadingData.title,
        mesh: treeData.mesh,
        bones: treeData.bones,
        referenceId: this.loadingData.referenceCategory && this.categoryReferences.length > 0 && this.loadingData.referenceId ? this.loadingData.referenceId : null,
      };
    }
    return null;
  }

  @Watch('templateId', { immediate: true })
  onTemplateIdChanged(): void {
    this.selectedInput.modelId = this.templateId;
    this.selectedInput.historyList.modelId = this.templateId;
    this.selectedInput.historyList.defaultUndoRedo = this;
  }

  @Watch('selectedInput.lastUpdateVisibility')
  async onSelectedInputVisibilityChanged(): Promise<void> {
    if (this.activeEditorMode === EditorModeState.bone) {
      if (this.selectedInput && this.selectedInput.treeData.length > 0) {
        this.activeBoneIndex = this.selectedInput.treeData[0].activeBoneIndex;
      }
    }
  }

  updateReferenceItem(referenceItem: MeshExportData): void {
    this.boneData.length = 0;
    for (const boneData of referenceItem.bones) {
      const bone = this.addBone(boneData.name);
      bone.position.copy(boneData.position.clone());
    }
  }

  @Watch('loadingData.referenceId', { immediate: true })
  onReferenceIdChanged(): void {
    if (this.templateId && this.loadingData.referenceId) {
      const referenceItem = this.referenceData.find((item) => item.componentId === this.loadingData.referenceId);
      if (referenceItem) {
        this.boneData.length = 0;
        for (const boneData of referenceItem.bones) {
          const bone = this.addBone(boneData.name);
          bone.position.copy(boneData.position.clone());
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async dragDone(event: any): Promise<void> {
    this.boneKey += 1;
    if (this.activeBoneIndex === event.oldIndex) {
      this.activeBoneIndex = event.newIndex;
    } else if (this.activeBoneIndex < event.oldIndex && this.activeBoneIndex >= event.newIndex) {
      this.activeBoneIndex++;
    } else if (this.activeBoneIndex > event.oldIndex && this.activeBoneIndex <= event.newIndex) {
      this.activeBoneIndex--;
    }
    this.selectedInput.updateVisibility();
  }

  @Watch('activeBoneIndex')
  onActiveBoneIndexChanged(): void {
    if (this.selectedInput && this.selectedInput.treeData.length > 0) {
      this.selectedInput.treeData[0].activeBoneIndex = this.activeBoneIndex;
      this.selectedInput.updateVisibility();
    }
  }

  getSelectionSize(): THREE.Vector3 {
    const meshEditor: MeshEditor = this.$refs.meshEditor as MeshEditor;
    if (meshEditor) {
      return meshEditor.getSelectionSize();
    }
    return new THREE.Vector3(10, 10, 10);
  }

  getSelectionFloorCenter(): THREE.Vector3 {
    const meshEditor: MeshEditor = this.$refs.meshEditor as MeshEditor;
    if (meshEditor) {
      return meshEditor.getSelectionFloorCenter();
    }
    return new THREE.Vector3(0, 0, 0);
  }

  addBone(name: string | null = null): THREE.Bone {
    let defaultBoneDistance = this.getSelectionSize().y;
    if (!defaultBoneDistance) defaultBoneDistance = 100;
    const bone = new THREE.Bone();
    bone.name = name ? name : (this.boneData.length + 1).toString();
    if (this.boneData.length === 0) {
      const pivot = this.getSelectionFloorCenter();
      bone.position.set(pivot.x, pivot.y, pivot.z);
    } else {
      const to = this.boneData[this.boneData.length - 1].position.clone();
      const from = this.boneData.length === 1 ? new THREE.Vector3(0, -1, 0) : this.boneData[this.boneData.length - 2].position.clone();
      const direction = to.clone().sub(from).normalize().setLength(defaultBoneDistance);
      bone.position.copy(to.clone().add(direction));
    }
    this.boneData.push(bone);
    this.selectedInput.updateVisibility();
    this.$nextTick(() => {
      this.activeBoneIndex = this.boneData.length - 1;
    });
    return bone;
  }

  removeBone(index: number): void {
    ElMessageBox.confirm((this as any).$t('confirm.delete.message'), (this as any).$t('confirm.delete.title'), {
      confirmButtonText: (this as any).$t('confirm.delete.ok'),
      cancelButtonText: (this as any).$t('confirm.delete.cancel'),
      type: 'warning',
    }).then(() => {
      this.boneData.splice(index, 1);
      this.selectedInput.updateVisibility();
    });
  }

  isFileType(filename: string, filetype: FileType3D): boolean {
    return getFileType(filename) === filetype;
  }

  @Watch('uploadProgress', { immediate: true })
  onUploadProgressChanged(): void {
    if (this.uploadProgress === UploadProgress.Select) {
      this.loadingDataValid = false;
    }
    if (this.previousUploadProgress === UploadProgress.Select && this.uploadProgress !== this.previousUploadProgress) {
      this.previousUploadProgress = this.uploadProgress;
      const dataForm = this.$refs.dataForm as typeof ElForm;
      dataForm?.validate(async (valid: boolean) => {
        this.loadingDataValid = valid;
        if (!valid) {
          this.$emit('update:uploadProgress', UploadProgress.Select);
          this.previousUploadProgress = UploadProgress.Select;
        }
      });
    }
    if (this.uploadProgress !== this.previousUploadProgress) {
      this.previousUploadProgress = this.uploadProgress;
    }
    if (this.uploadProgress === UploadProgress.Pivot) {
      if (this.boneData.length <= 0) {
        this.addBone((this as any).$t('components.mesh-loader.pivotBone'));
      }
      if (this.boneData.length <= 1) {
        this.addBone((this as any).$t('components.mesh-loader.secondBone'));
      }
    }

    switch (this.uploadProgress) {
      case UploadProgress.Select:
        this.activeEditorMode = EditorModeState.readonly;
        break;
      case UploadProgress.Orientation:
        this.activeEditorMode = EditorModeState.orientation;
        break;
      case UploadProgress.Pivot:
        this.activeEditorMode = EditorModeState.bone;
        break;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async uploadFile(res: any): Promise<boolean> {
    const url = URL.createObjectURL(res.file);
    const data = { name: res.file.name, url: url, uid: res.file.uid };
    setTimeout(() => {
      this.addMesh(data);
      this.handleSelect(data);
    }, 500);
    return true;
  }

  handleRemove(file: UploadFile): void {
    ElMessageBox.confirm((this as any).$t('components.mesh-loader.deleteMessage'), 'info', {
      confirmButtonText: (this as any).$t('components.mesh-loader.yes'),
      cancelButtonText: (this as any).$t('components.mesh-loader.no'),
      type: 'warning',
    })
      .then(async (confirm) => {
        if (confirm) {
          const item = this.selectedInput.treeData.find((x) => x.name === file.uid.toString());
          if (item) {
            this.selectedInput.removeItem(item);
          }
          this.handleSelect(null);
          this.meshFiles = cloneDeep(this.meshFiles).filter((f) => f.uid !== file?.uid);
        }
      })
      .catch(() => {
        //
      });
  }

  addMesh(file: UploadData | null): void {
    if (file) {
      const fileType = getFileType(file.name) as FileType3D;
      if (fileType !== FileType3D.NONE) {
        this.selected = file.uid;
        if (file.url) {
          const item = this.selectedInput.addImport(file.uid.toString(), file.name, file.url, '', '', null, true);
          item.bones = this.boneData;
          item.activeBoneIndex = this.activeBoneIndex;
          this.loadingData.url = file.url;
          this.loadingData.fileType = fileType;
        }
      }
    }
  }

  handleSelect(file: UploadData | null): void {
    if (file) {
      const fileType = getFileType(file.name) as FileType3D;
      if (fileType !== FileType3D.NONE) {
        this.selected = file.uid;
        if (file.url) {
          this.selectedInput.clear();
          const item = this.selectedInput.addImport(file.uid.toString(), file.name, file.url, '', '');
          item.bones = this.boneData;
          item.activeBoneIndex = this.activeBoneIndex;
          this.loadingData.url = file.url;
          this.loadingData.fileType = fileType;
        }
      }
    } else {
      this.selected = -1;
      this.selectedInput.clear();
      const troisRenderer: any = this.$refs.troisRenderer;
      const three: any = troisRenderer.three;
      cleanUpScene(three.scene);
    }
  }

  beforeUpload(file: any): boolean {
    if (isValidFileType(file.name)) {
      return true;
    }
    ElMessage.error((this as any).$t('components.mesh-loader.wrongType'));
    return false;
  }

  async defaultUndo(): Promise<void> {
    //
  }

  async defaultRedo(): Promise<void> {
    //
  }

  setShowFormBuilder(): void {
    this.formbuilderDialog.show();
  }

  uploadObject3D(type3D: FileType3D, data: UploadData) {
    setTimeout(() => {
      if (type3D !== FileType3D.NONE) {
        this.selected = data.uid;

        if (data.url) {
          const item = this.selectedInput.addImport(data.uid.toString(), data.name, data.url, '', '', null, true);
          item.bones = this.boneData;
          item.activeBoneIndex = this.activeBoneIndex;
        }
      }

      this.handleSelect(data);
    }, 500);
  }

  removeObject3D(uid: number) {
    const item = this.selectedInput.treeData.find((x) => x.name === uid.toString());
    if (item) {
      this.selectedInput.removeItem(item);
    }
    this.handleSelect(null);
  }
}
</script>

<style lang="scss" scoped>
.el-scrollbar :deep(.el-scrollbar__view) {
  margin: 1rem;
}

.el-steps :deep(.el-steps--vertical) {
  height: unset;
}

.el-steps :deep(.el-step__head.is-process) {
  color: var(--el-color-primary);
  border-color: var(--el-color-primary);
}

.el-steps :deep(.el-step__head.is-finish .el-step__head.is-wait) {
  color: var(--el-text-color-primary);
  border-color: var(--el-text-color-primary);
}

.big {
  margin-bottom: 0.5rem;
}

.upload-item {
  overflow: visible;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.selected {
  border: var(--color-primary) 2px solid;
}

.bones {
  min-height: calc(100vh - 7.2rem);
}

.addButton {
  text-align: right;
  margin-top: 1rem;
}

.action {
  color: var(--color-primary);
}

.el-aside {
  z-index: 1;
}
</style>
